import { Close } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { format, isBefore, isSameDay, setHours, setMinutes, subDays } from 'date-fns';
import React, { Fragment, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useActiveClassroom } from '../active-simulation/useActiveSimulation';
import ksimCoolDude from '../assets/ksim-cool-dude.svg';
import EndExerciseDialog from '../common/dialogs/EndExerciseDialog';
import ExerciseCard from '../exercise-card/ExerciseCard';
import Fallback from '../fallback/Fallback';
import { ExerciseState, ExerciseSummary } from '../types';
import { Pages } from '../types/pages';

const formatHeader = (date: Date) =>
  isSameDay(new Date(), date)
    ? 'Today'
    : isSameDay(new Date(), subDays(date, 1))
    ? 'Tomorrow'
    : format(date, 'EEEE d LLLL');

const roundDay = (date: Date) => setMinutes(setHours(date, 0), 0);

export interface ExerciseListProps {
  exercises: ExerciseSummary[];
  exerciseState: ExerciseState;
  onEndExercise?: (exercise?: ExerciseSummary) => void;
}

const ScheduledExerciseList = ({ exercises, exerciseState, onEndExercise }: ExerciseListProps) => {
  const { pathname } = useLocation();
  const basePath = pathname.split('/').slice(0, 2).join('/');
  const [exercise, setExercise] = useState<ExerciseSummary>();
  const { activeClassroom } = useActiveClassroom();
  const [confirmDialog, setShowConfirmDialog] = useState(false);

  const exercisesMap: Record<string, ExerciseSummary[]> | undefined = exercises.reduce((acc, exercise) => {
    // If start time passed, add to 'Today'
    const today = roundDay(new Date());
    let roundedStartTime = roundDay(exercise.startTime);
    if (isBefore(roundedStartTime, today)) {
      roundedStartTime = today;
    }

    // Add to bin
    const startTimeKey = format(roundedStartTime, 'P');
    acc[startTimeKey] = [...(startTimeKey in acc ? acc[startTimeKey] : []), exercise];

    return acc;
  }, {});

  const onCompleted = () => {
    setShowConfirmDialog(false);
    onEndExercise && onEndExercise(exercise);
  };

  const onError = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      {exercises.length ? (
        Object.entries(exercisesMap).map(([key, exercises]) => (
          <Fragment key={key}>
            {exerciseState === ExerciseState.Active && (
              <Typography component="h3" variant="h6" mb={3}>
                {formatHeader(new Date(key))}
              </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
              {exercises?.map(exercise => (
                <ExerciseCard
                  {...exercise}
                  key={exercise.id}
                  to={`../${exercise?.assigned ? `${Pages.Learn}` : `${basePath}`}/exercises/${exercise.id}`}
                  showTimeDetails
                  exerciseState={exerciseState}
                  onAction={
                    onEndExercise && (
                      <IconButton
                        disabled={exercise.id === activeClassroom?.exercise.id}
                        size="small"
                        onClick={e => {
                          setExercise(exercise);
                          setShowConfirmDialog(true);

                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        title={`End ${exercise.title}`}
                      >
                        <Close />
                      </IconButton>
                    )
                  }
                />
              ))}
            </Box>
          </Fragment>
        ))
      ) : (
        <Fallback imgSrc={ksimCoolDude} message="You have no scheduled exercises" />
      )}

      {exercise?.id && (
        <EndExerciseDialog
          exerciseId={exercise.id}
          onClose={() => setShowConfirmDialog(false)}
          onCompleted={onCompleted}
          onDeleted={onCompleted}
          onError={onError}
          open={confirmDialog}
        />
      )}
    </>
  );
};

export default ScheduledExerciseList;
